import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Client {
  id: string;
  name: string;
  category: string;
}

interface MetricValue {
  current: number;
  change_pct: number | null;
}

interface ClientScorecard {
  client_id: string;
  client_name: string;
  category: 'BH' | 'Non-BH';
  organic_clicks: MetricValue;
  gmb_call_clicks: MetricValue;
  other_search_engines: MetricValue;
  ai_referrals: MetricValue;
  total_users: MetricValue;
  backlinks: MetricValue;
  site_health_score: MetricValue;
}

type PresetKey = '7d' | '28d' | '30d' | '60d' | '90d' | 'custom';
type TabKey = 'BH' | 'Non-BH';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

function fmtNumber(v: number): string {
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (v >= 1_000) return (v / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return v.toLocaleString();
}

// ─── StatBadge ───────────────────────────────────────────────────────────────

function StatBadge({ changePct }: { changePct: number | null }) {
  if (changePct === null || changePct === undefined) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
        — N/A
      </span>
    );
  }

  const isPositive = changePct > 0;
  const isNeutral = changePct === 0;

  if (isNeutral) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
        — 0%
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${
        isPositive
          ? 'bg-emerald-50 text-emerald-700'
          : 'bg-red-50 text-red-700'
      }`}
    >
      {isPositive ? '↑' : '↓'} {Math.abs(changePct).toFixed(1)}%
    </span>
  );
}

// ─── Metric Row ──────────────────────────────────────────────────────────────

const METRIC_CONFIG: {
  key: keyof Omit<ClientScorecard, 'client_id' | 'client_name' | 'category'>;
  label: string;
  icon: string;
}[] = [
  { key: 'organic_clicks', label: 'Organic Clicks (GSC)', icon: '🔍' },
  { key: 'gmb_call_clicks', label: 'GMB Call Clicks', icon: '📞' },
  { key: 'other_search_engines', label: 'Other Search Engines', icon: '🌐' },
  { key: 'ai_referrals', label: 'AI Referrals', icon: '🤖' },
  { key: 'total_users', label: 'Total Users', icon: '👥' },
  { key: 'backlinks', label: 'Backlinks', icon: '🔗' },
  { key: 'site_health_score', label: 'Site Health Score', icon: '💚' },
];

function MetricRow({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: MetricValue;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span className="text-base">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-900">
          {fmtNumber(value.current)}
        </span>
        <StatBadge changePct={value.change_pct} />
      </div>
    </div>
  );
}

// ─── Skeleton Card ───────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-xl border border-gray-200 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="h-5 w-36 rounded bg-gray-200" />
        <div className="h-5 w-16 rounded-full bg-gray-200" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="h-4 w-32 rounded bg-gray-100" />
            <div className="flex gap-2">
              <div className="h-4 w-12 rounded bg-gray-100" />
              <div className="h-4 w-14 rounded-full bg-gray-100" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Category Badge ──────────────────────────────────────────────────────────

function CategoryBadge({ category }: { category: string }) {
  const isBh = category.toLowerCase() === 'bh';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        isBh
          ? 'bg-blue-100 text-blue-800'
          : 'bg-purple-100 text-purple-800'
      }`}
    >
      {isBh ? 'BH' : 'Non-BH'}
    </span>
  );
}

// ─── Refresh icon button ─────────────────────────────────────────────────────

function RefreshButton({ loading, onClick }: { loading: boolean; onClick: (e: React.MouseEvent) => void }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      title="Fetch latest data for this client"
      className={`rounded-full p-1 transition-colors ${
        loading
          ? 'text-blue-400 cursor-not-allowed'
          : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
      }`}
    >
      <svg
        className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    </button>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function SEODashboardPage() {
  const navigate = useNavigate();

  // ── Clients ──
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [sidebarSearch, setSidebarSearch] = useState('');

  // ── Tab ──
  const [activeTab, setActiveTab] = useState<TabKey>('BH');

  // ── Date range ──
  const [preset, setPreset] = useState<PresetKey>('30d');
  const [dateFrom, setDateFrom] = useState(formatDate(daysAgo(30)));
  const [dateTo, setDateTo] = useState(formatDate(daysAgo(1)));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // ── Scorecards ──
  const [scorecards, setScorecards] = useState<Record<string, ClientScorecard>>({});
  const [scorecardsLoading, setScorecardsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cachedAt, setCachedAt] = useState<string | null>(null);

  // ── Per-client fetch state ──
  const [fetchingClients, setFetchingClients] = useState<Set<string>>(new Set());
  const [fetchingAll, setFetchingAll] = useState(false);
  const [fetchAllProgress, setFetchAllProgress] = useState<{ done: number; total: number } | null>(null);
  const fetchAllAbortRef = useRef(false);

  // Close date picker on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) {
        setShowDatePicker(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Apply preset
  const applyPreset = useCallback(
    (key: PresetKey) => {
      setPreset(key);
      if (key !== 'custom') {
        const days = { '7d': 7, '28d': 28, '30d': 30, '60d': 60, '90d': 90 }[key];
        setDateFrom(formatDate(daysAgo(days)));
        setDateTo(formatDate(daysAgo(1)));
        setShowDatePicker(false);
        // Reset auto-fetch tracking so new range can trigger auto-fetch if stale
        autoFetchTriggeredRef.current = null;
      }
    },
    [],
  );

  // ── Fetch clients ──
  useEffect(() => {
    let cancelled = false;
    setClientsLoading(true);
    api
      .get('/seo-dashboard/clients')
      .then((res) => {
        if (!cancelled) {
          const data = res.data;
          setClients(Array.isArray(data) ? data : data.clients || []);
        }
      })
      .catch((err) => {
        if (!cancelled) setError('Failed to load client list: ' + (err?.response?.data?.detail || err.message));
      })
      .finally(() => {
        if (!cancelled) setClientsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Track whether we already triggered an auto-fetch for the current date range
  const autoFetchTriggeredRef = useRef<string | null>(null);

  // ── Load cached scorecards on mount / date change ──
  useEffect(() => {
    if (clients.length === 0) return;
    let cancelled = false;
    setScorecardsLoading(true);
    setError(null);

    api
      .get('/seo-dashboard/cached-scorecards', { params: { from: dateFrom, to: dateTo } })
      .then((res) => {
        if (cancelled) return;
        const { cached, data } = res.data;
        if (cached && data?.scorecards && Object.keys(data.scorecards).length > 0) {
          setScorecards(data.scorecards);
          setCachedAt(data.fetched_at);

          // Auto-refresh if cache is stale (>24 hours)
          const rangeKey = `${dateFrom}_${dateTo}`;
          if (data.stale && autoFetchTriggeredRef.current !== rangeKey) {
            autoFetchTriggeredRef.current = rangeKey;
            // Delay slightly so UI renders cached data first
            setTimeout(() => {
              if (!cancelled) fetchAll();
            }, 500);
          }
        } else {
          setScorecards({});
          setCachedAt(null);
        }
        setScorecardsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError('Failed to load data: ' + (err?.response?.data?.detail || err.message));
        setScorecardsLoading(false);
      });

    return () => { cancelled = true; };
  }, [clients, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch single client (all preset ranges: 7d, 30d, 60d, 90d) ──
  const fetchClient = useCallback(async (clientId: string) => {
    setFetchingClients((prev) => new Set(prev).add(clientId));
    try {
      const res = await api.post(`/seo-dashboard/fetch-client-all-ranges/${clientId}`, null, {
        timeout: 180000,
      });
      const sc = res.data as ClientScorecard;
      // Update the scorecard for current view
      setScorecards((prev) => ({ ...prev, [clientId]: sc }));
      setCachedAt(new Date().toISOString());
    } catch (err: any) {
      console.error(`Failed to fetch ${clientId}:`, err);
    } finally {
      setFetchingClients((prev) => {
        const next = new Set(prev);
        next.delete(clientId);
        return next;
      });
    }
  }, []);

  // ── Fetch all clients in parallel batches (3 at a time to respect API limits) ──
  const PARALLEL_BATCH_SIZE = 3;

  const fetchAll = useCallback(async () => {
    if (fetchingAll) return;
    setFetchingAll(true);
    fetchAllAbortRef.current = false;
    const total = clients.length;
    let done = 0;
    setFetchAllProgress({ done: 0, total });

    // Process clients in batches of PARALLEL_BATCH_SIZE
    for (let i = 0; i < clients.length; i += PARALLEL_BATCH_SIZE) {
      if (fetchAllAbortRef.current) break;
      const batch = clients.slice(i, i + PARALLEL_BATCH_SIZE);

      // Mark all batch clients as fetching
      setFetchingClients((prev) => {
        const next = new Set(prev);
        batch.forEach((c) => next.add(c.id));
        return next;
      });

      // Fetch batch in parallel
      const results = await Promise.allSettled(
        batch.map((c) =>
          api.post(`/seo-dashboard/fetch-client-all-ranges/${c.id}`, null, { timeout: 180000 })
            .then((res) => ({ id: c.id, sc: res.data as ClientScorecard }))
        )
      );

      // Apply results + clear fetching state
      for (const result of results) {
        if (result.status === 'fulfilled') {
          setScorecards((prev) => ({ ...prev, [result.value.id]: result.value.sc }));
        } else {
          console.error('Batch fetch failed:', result.reason);
        }
      }
      setFetchingClients((prev) => {
        const next = new Set(prev);
        batch.forEach((c) => next.delete(c.id));
        return next;
      });
      done += batch.length;
      setFetchAllProgress({ done: Math.min(done, total), total });
    }

    // Reload cached data for the currently selected range (all ranges are now cached)
    try {
      const res = await api.get('/seo-dashboard/cached-scorecards', { params: { from: dateFrom, to: dateTo } });
      const { cached, data } = res.data;
      if (cached && data?.scorecards) {
        setScorecards(data.scorecards);
        setCachedAt(data.fetched_at);
      }
    } catch { /* ignore — scorecards already updated inline above */ }

    // Mark this range as fetched so auto-fetch doesn't re-trigger
    autoFetchTriggeredRef.current = `${dateFrom}_${dateTo}`;
    setFetchingAll(false);
    setFetchAllProgress(null);
  }, [fetchingAll, clients, dateFrom, dateTo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { fetchAllAbortRef.current = true; };
  }, []);

  // ── Derived data ──
  const filteredSidebarClients = useMemo(
    () =>
      clients.filter((c) =>
        c.name.toLowerCase().includes(sidebarSearch.toLowerCase()),
      ),
    [clients, sidebarSearch],
  );

  const isBH = (cat: string) => cat.toLowerCase() === 'bh';

  const tabClients = useMemo(() => {
    return clients.filter((c) =>
      activeTab === 'BH' ? isBH(c.category) : !isBH(c.category),
    );
  }, [clients, activeTab]);

  const bhCount = useMemo(() => clients.filter((c) => isBH(c.category)).length, [clients]);
  const nonBhCount = useMemo(() => clients.filter((c) => !isBH(c.category)).length, [clients]);

  // ── Render ──
  return (
    <Layout>
      <div className="flex h-[calc(100vh-64px)] overflow-hidden">
        {/* ── Sidebar ── */}
        <aside className="flex w-60 flex-shrink-0 flex-col border-r border-gray-200 bg-gray-50">
          <div className="border-b border-gray-200 p-3">
            <h2 className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">
              Clients
            </h2>
            <input
              type="text"
              placeholder="Search clients..."
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {clientsLoading ? (
              <div className="space-y-2 p-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-8 animate-pulse rounded bg-gray-200" />
                ))}
              </div>
            ) : filteredSidebarClients.length === 0 ? (
              <p className="p-4 text-center text-sm text-gray-400">No clients found.</p>
            ) : (
              <ul className="py-1">
                {filteredSidebarClients.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => navigate(`/seo-dashboard/${c.id}`)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      <span className="truncate font-medium">{c.name}</span>
                      <CategoryBadge category={c.category} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-gray-200 px-3 py-2 text-xs text-gray-400">
            {clients.length} client{clients.length !== 1 ? 's' : ''}
          </div>
        </aside>

        {/* ── Main Content ── */}
        <main className="flex flex-1 flex-col overflow-hidden">
          {/* Header */}
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-6 py-4">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900">SEO Dashboard</h1>
              <button
                onClick={fetchAll}
                disabled={fetchingAll}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  fetchingAll
                    ? 'bg-blue-100 text-blue-600 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {fetchingAll ? (
                  <>
                    <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Fetching {fetchAllProgress ? `(${fetchAllProgress.done}/${fetchAllProgress.total})` : '...'}
                  </>
                ) : (
                  <>
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Fetch All Ranges
                  </>
                )}
              </button>
              {cachedAt && !fetchingAll && (() => {
                const ageHours = (Date.now() - new Date(cachedAt).getTime()) / 3600000;
                const ageStr = ageHours < 1
                  ? `${Math.round(ageHours * 60)}m ago`
                  : ageHours < 24
                  ? `${Math.round(ageHours)}h ago`
                  : `${Math.round(ageHours / 24)}d ago`;
                const isStale = ageHours > 24;
                return (
                  <span className={`text-xs ${isStale ? 'text-amber-600' : 'text-gray-400'}`}>
                    {isStale ? '⚠ Stale — ' : ''}Updated {ageStr}
                    {isStale && ' (auto-refreshing...)'}
                  </span>
                );
              })()}
            </div>

            {/* Date range picker */}
            <div className="relative" ref={datePickerRef}>
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
              >
                <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {dateFrom} → {dateTo}
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showDatePicker && (
                <div className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Presets
                  </p>
                  <div className="mb-3 flex gap-2">
                    {(['7d', '28d', '30d', '60d', '90d'] as PresetKey[]).map((k) => (
                      <button
                        key={k}
                        onClick={() => applyPreset(k)}
                        className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                          preset === k
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Custom Range
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setPreset('custom');
                      }}
                      className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <span className="text-gray-400">→</span>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setPreset('custom');
                      }}
                      className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  {preset === 'custom' && (
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="mt-3 w-full rounded-lg bg-blue-600 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 transition-colors"
                    >
                      Apply
                    </button>
                  )}
                </div>
              )}
            </div>
          </header>

          {/* Tabs */}
          <div className="border-b border-gray-200 bg-white px-6">
            <nav className="-mb-px flex gap-6">
              {([
                { key: 'BH' as TabKey, label: 'BH Clients', count: bhCount },
                { key: 'Non-BH' as TabKey, label: 'Non-BH Clients', count: nonBhCount },
              ]).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`whitespace-nowrap border-b-2 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab.key
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                  <span
                    className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      activeTab === tab.key
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Error banner */}
          {error && (
            <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Scorecard Grid */}
          <div className="flex-1 overflow-y-auto p-6">
            {scorecardsLoading || clientsLoading ? (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {tabClients.map((client) => {
                  const sc = scorecards[client.id];
                  const isClientFetching = fetchingClients.has(client.id);

                  // No cached data — show placeholder card with fetch button
                  if (!sc) {
                    return (
                      <div
                        key={client.id}
                        className="rounded-xl border border-gray-200 bg-white p-5"
                      >
                        <div className="mb-3 flex items-center justify-between">
                          <h3 className="truncate text-sm font-bold text-gray-900">
                            {client.name}
                          </h3>
                          <div className="flex items-center gap-2">
                            <CategoryBadge category={client.category} />
                            <RefreshButton
                              loading={isClientFetching}
                              onClick={(e) => { e.stopPropagation(); fetchClient(client.id); }}
                            />
                          </div>
                        </div>
                        <div className="border-t border-gray-100 pt-3">
                          {isClientFetching ? (
                            <div className="flex items-center justify-center gap-2 py-8 text-sm text-blue-500">
                              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Fetching data...
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center py-6 text-gray-400">
                              <p className="text-xs">No data yet</p>
                              <button
                                onClick={() => fetchClient(client.id)}
                                className="mt-2 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 transition-colors"
                              >
                                Fetch Stats
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Has data — show full scorecard
                  return (
                    <div
                      key={client.id}
                      onClick={() => navigate(`/seo-dashboard/${client.id}`)}
                      className="cursor-pointer rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-all hover:border-blue-300 hover:shadow-md"
                    >
                      {/* Card header */}
                      <div className="mb-3 flex items-center justify-between">
                        <h3 className="truncate text-sm font-bold text-gray-900">
                          {sc.client_name}
                        </h3>
                        <div className="flex items-center gap-2">
                          <CategoryBadge category={sc.category} />
                          <RefreshButton
                            loading={isClientFetching}
                            onClick={(e) => { e.stopPropagation(); fetchClient(client.id); }}
                          />
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="mb-2 border-t border-gray-100" />

                      {/* Metrics */}
                      <div className="space-y-0.5">
                        {METRIC_CONFIG.map((m) => (
                          <MetricRow
                            key={m.key}
                            icon={m.icon}
                            label={m.label}
                            value={sc[m.key] as MetricValue}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </Layout>
  );
}
